import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  Button,
  Grid,
  Card,
  CardMedia,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Info as InfoIcon,
  CloudUpload as UploadIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { getRecordDetail, getImageUrl } from '../api/records';
import { uploadAndAnalyze } from '../api/imageQuality';
import { getStockOnHand } from '../api/soh';
import QualityResults from '../components/QualityResults';
import ImageUpload from '../components/ImageUpload';

export default function RecordDetail() {
  const navigate = useNavigate();
  const { itemCode, imageType } = useParams();
  const [record, setRecord] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [sohData, setSohData] = useState(null);
  const [sohLoading, setSohLoading] = useState(false);

  useEffect(() => {
    fetchRecordDetail();
    fetchStockOnHand();
  }, [itemCode, imageType]);

  const fetchStockOnHand = async () => {
    try {
      setSohLoading(true);
      const result = await getStockOnHand(itemCode);
      
      if (result.success) {
        setSohData(result.data);
      }
    } catch (err) {
      console.error('Failed to fetch SOH data:', err);
    } finally {
      setSohLoading(false);
    }
  };

  const fetchRecordDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getRecordDetail(itemCode, imageType);
      
      if (result.success) {
        setRecord(result.data);
        // Fetch signed URL for the image
        try {
          const signedUrl = await getImageUrl(itemCode, imageType);
          setImageUrl(signedUrl);
        } catch (urlError) {
          console.error('Failed to fetch image URL:', urlError);
          setImageUrl(null);
        }
      } else {
        setError(result.error || 'Failed to fetch record detail');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to fetch record detail');
    } finally {
      setLoading(false);
    }
  };

  const handleReplace = () => {
    setReplaceDialogOpen(true);
  };

  const handleCloseReplaceDialog = () => {
    setReplaceDialogOpen(false);
  };

  const handleUploadSuccess = async (file, metadata, onProgress) => {
    try {
      // Use the same item code and image type for replacement
      const replaceMetadata = {
        brand: record.brand_code,
        itemCode: record.item_code,
        type: record.image_type
      };

      const result = await uploadAndAnalyze(file, replaceMetadata, onProgress);

      if (result.success) {
        setSnackbarMessage('Image replaced and re-analyzed successfully!');
        setSnackbarOpen(true);
        setReplaceDialogOpen(false);
        // Refresh the record detail
        setTimeout(() => {
          fetchRecordDetail();
        }, 1000);
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      throw err;
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !record) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          {error || 'Record not found'}
        </Alert>
        <Button
          variant="contained"
          startIcon={<BackIcon />}
          onClick={() => navigate('/records')}
          sx={{ mt: 2 }}
        >
          Back to Records
        </Button>
      </Container>
    );
  }

  // Construct analysis data for QualityResults component
  // Reconstruct checks based on stored scores
  const brightnessScore = record.brightness_score || 0;
  const sharpnessScore = record.sharpness_score || 0;
  const contrastScore = record.contrast_score || 0;
  
  const analysisData = {
    qualityScores: {
      brightness: brightnessScore,
      sharpness: sharpnessScore,
      contrast: contrastScore,
      foregroundQuality: record.foreground_quality || 0,
      backgroundQuality: record.background_quality || 0
    },
    centering: {
      isCentered: record.is_centered,
      confidence: record.centering_confidence || 0,
      offsetX: record.offset_x?.toString() || '0',
      offsetY: record.offset_y?.toString() || '0',
      coverage: record.coverage_percentage?.toString() || '0',
      appropriateSize: record.appropriate_size,
      message: record.centering_message || 'No message available'
    },
    socialMediaReady: {
      ready: record.social_media_ready,
      score: record.overall_score?.toString() || '0',
      message: record.readiness_message || 'No message available',
      checks: {
        brightness: brightnessScore >= 60 && brightnessScore <= 80,
        sharpness: sharpnessScore >= 70,
        contrast: contrastScore >= 50,
        centered: record.is_centered && record.appropriate_size
      }
    },
    detectedLabels: record.detected_labels || [],
    recommendations: record.recommendations || []
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Button
            color="inherit"
            startIcon={<BackIcon />}
            onClick={() => navigate('/records')}
            sx={{ mr: 2 }}
          >
            Back to List
          </Button>
          <InfoIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Record Detail - {itemCode} ({imageType})
          </Typography>
          <Button
            color="inherit"
            startIcon={<UploadIcon />}
            onClick={handleReplace}
          >
            Replace Image
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box>
          {/* Image Preview */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card>
                {imageUrl ? (
                  <CardMedia
                    component="img"
                    image={imageUrl}
                    alt={`${record.item_code} - ${record.image_type}`}
                    sx={{ 
                      maxHeight: 500, 
                      objectFit: 'contain',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.9
                      }
                    }}
                    onClick={() => window.open(imageUrl, '_blank')}
                    title="Click to view full size image in new tab"
                  />
                ) : (
                  <Box 
                    sx={{ 
                      height: 500, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      bgcolor: 'grey.100'
                    }}
                  >
                    <Typography color="text.secondary">
                      Image not available
                    </Typography>
                  </Box>
                )}
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Image Information
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Item Code:</strong> {record.item_code}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Brand:</strong> {record.brand_code}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Image Type:</strong> {record.image_type}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>S3 Bucket:</strong> {record.s3_bucket}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                    <strong>S3 Key:</strong> {record.s3_key}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Analyzed At:</strong> {formatDate(record.analyzed_at)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Modified Date:</strong> {formatDate(record.modified_date)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Stock On Hand Section */}
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <InventoryIcon sx={{ mr: 1 }} color="primary" />
                    <Typography variant="h6">
                      Stock On Hand
                    </Typography>
                  </Box>

                  {sohLoading ? (
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : sohData ? (
                    <>
                      {/* Summary Cards */}
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6} sm={3}>
                          <Paper elevation={1} sx={{ p: 1.5, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                              Warehouses
                            </Typography>
                            <Typography variant="h6">
                              {sohData.summary?.warehouse_count || 0}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Paper elevation={1} sx={{ p: 1.5, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                              Total Stock
                            </Typography>
                            <Typography variant="h6">
                              {parseFloat(sohData.summary?.total_stock || 0).toFixed(0)}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Paper elevation={1} sx={{ p: 1.5, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                              Hold Stock
                            </Typography>
                            <Typography variant="h6" color="warning.main">
                              {parseFloat(sohData.summary?.total_hold_stock || 0).toFixed(0)}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Paper elevation={1} sx={{ p: 1.5, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                              Available
                            </Typography>
                            <Typography variant="h6" color="success.main">
                              {parseFloat(sohData.summary?.total_available_stock || 0).toFixed(0)}
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>

                      {/* Detailed Table */}
                      {sohData.details && sohData.details.length > 0 ? (
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Warehouse</strong></TableCell>
                                <TableCell><strong>Location</strong></TableCell>
                                <TableCell align="right"><strong>Stock</strong></TableCell>
                                <TableCell align="right"><strong>Hold</strong></TableCell>
                                <TableCell align="right"><strong>Available</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sohData.details.map((row, index) => (
                                <TableRow key={index} hover>
                                  <TableCell>
                                    <Box>
                                      <Typography variant="body2" fontWeight="medium">
                                        {row.wh_code}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {row.wh_desc}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {row.location || 'N/A'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2">
                                      {parseFloat(row.stock || 0).toFixed(0)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    {parseFloat(row.hold_stock || 0) > 0 ? (
                                      <Chip 
                                        label={parseFloat(row.hold_stock).toFixed(0)} 
                                        size="small" 
                                        color="warning"
                                        sx={{ minWidth: 50 }}
                                      />
                                    ) : (
                                      <Typography variant="body2">0</Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography 
                                      variant="body2" 
                                      fontWeight="medium"
                                      color={parseFloat(row.available_stock || 0) > 0 ? 'success.main' : 'text.secondary'}
                                    >
                                      {parseFloat(row.available_stock || 0).toFixed(0)}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Alert severity="info">No stock on hand data available for this item.</Alert>
                      )}
                    </>
                  ) : (
                    <Alert severity="info">No stock on hand data available.</Alert>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Quality Results */}
          <QualityResults analysisData={analysisData} />
        </Box>
      </Container>

      {/* Replace Image Dialog */}
      <Dialog 
        open={replaceDialogOpen} 
        onClose={handleCloseReplaceDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Replace Image: {itemCode} - {imageType}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Upload a new image to replace the existing one. The system will automatically re-analyze the quality.
              Brand, Item Code, and Image Type cannot be changed.
            </Alert>
            <ImageUpload
              onUploadSuccess={handleUploadSuccess}
              onUploadError={(err) => {
                setError(err.message || 'Upload failed');
              }}
              fixedMetadata={{
                brand: record.brand_code,
                itemCode: record.item_code,
                type: record.image_type
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReplaceDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
